import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Stack,
  FormHelperText,
  Alert
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import {
  updateUnavailableBlock,
  deleteUnavailableBlock,
  getUnavailableBlockTypes
} from '../../api/calendarService';
import { addHours } from 'date-fns';
import { enAU } from 'date-fns/locale';
import { UnavailableBlock } from '../../models/calendar';
import { useTimezone } from '../../context/TimezoneContext';
import { convertToTimezone, convertFromTimezone, convertDisplayTimeToPractitioner } from '../../utils/timezone';

interface UnavailableBlockEditDialogProps {
  open: boolean;
  onClose: () => void;
  block: UnavailableBlock | null;
  practitioners: any[];
  onBlockUpdated: () => void;
  onBlockDeleted: () => void;
}

const UnavailableBlockEditDialog: React.FC<UnavailableBlockEditDialogProps> = ({
  open,
  onClose,
  block,
  practitioners,
  onBlockUpdated,
  onBlockDeleted
}) => {
  const { selectedTimezone, getPractitionerTimezone } = useTimezone();
  
  // Form state
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [blockTypeId, setBlockTypeId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [localSelectedDate, setLocalSelectedDate] = useState<Date | null>(null);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string>('');
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [blockTypes, setBlockTypes] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);

  // Initialize form values when dialog opens
  useEffect(() => {
    if (open && block) {
      console.log('🔧 UnavailableBlockEditDialog opening with:', {
        id: block.id,
        starts_at: block.starts_at,
        ends_at: block.ends_at,
        practitioner_id: block.practitioner_id,
        selectedTimezone,
        raw_start: new Date(block.starts_at).toLocaleString(),
        raw_end: new Date(block.ends_at).toLocaleString()
      });
      
      // The block times are stored in practitioner timezone
      // We need to convert them to display timezone for editing
      const practitionerTimezone = getPractitionerTimezone(block.practitioner_id || '') || 'Australia/Adelaide';
      
      // Convert from practitioner timezone to display timezone
      let start = new Date(block.starts_at);
      let end = new Date(block.ends_at);
      
      if (practitionerTimezone !== selectedTimezone) {
        console.log(`🔧 Converting edit times from ${practitionerTimezone} to ${selectedTimezone}`);
        
        start = convertToTimezone(
          convertFromTimezone(start, practitionerTimezone),
          selectedTimezone
        );
        
        end = convertToTimezone(
          convertFromTimezone(end, practitionerTimezone),
          selectedTimezone
        );
        
        console.log(`🔧 Converted start: ${start.toLocaleString()}, end: ${end.toLocaleString()}`);
      }
      
      setStartDate(start);
      setLocalSelectedDate(start);
      setEndDate(end);
      
      console.log('🔧 Final edit dialog times:', {
        start: start.toLocaleString(),
        end: end.toLocaleString(),
        startValue: `${start.getHours()}:${start.getMinutes() || 0}`,
        endValue: `${end.getHours()}:${end.getMinutes() || 0}`
      });
      
      // Set block type
      setBlockTypeId(block.unavailable_block_type_id || '');
      
      // Set notes
      setNotes(block.notes || '');
      
      // Set practitioner
      setSelectedPractitionerId(block.practitioner_id || '');
      
      // Reset error state
      setError(null);
      setDeleteConfirm(false);
    }
  }, [open, block, selectedTimezone]);

  // Fetch unavailable block types when dialog opens
  useEffect(() => {
    if (open) {
      fetchBlockTypes();
    }
  }, [open]);

  // Fetch unavailable block types
  const fetchBlockTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await getUnavailableBlockTypes();
      console.log('Unavailable block types response:', response);
      
      // If no types are returned, create default types
      if (!response.unavailable_block_types || response.unavailable_block_types.length === 0) {
        setBlockTypes([
          { id: 'lunch', name: 'Lunch Break', color: '#f44336', duration_in_minutes: 60 },
          { id: 'meeting', name: 'Meeting', color: '#ff9800', duration_in_minutes: 30 },
          { id: 'out', name: 'Out of Office', color: '#9c27b0', duration_in_minutes: 240 }
        ]);
      } else {
        setBlockTypes(response.unavailable_block_types);
      }
    } catch (error) {
      console.error('Error fetching unavailable block types:', error);
      // Set default types on error
      setBlockTypes([
        { id: 'lunch', name: 'Lunch Break', color: '#f44336', duration_in_minutes: 60 },
        { id: 'meeting', name: 'Meeting', color: '#ff9800', duration_in_minutes: 30 },
        { id: 'out', name: 'Out of Office', color: '#9c27b0', duration_in_minutes: 240 }
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    if (!startDate || !endDate) {
      setError('Start and end times are required');
      return;
    }

    if (endDate <= startDate) {
      setError('End time must be after start time');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!block) {
        throw new Error('No block data to update');
      }

      // Convert times from display timezone back to practitioner timezone before saving
      const practitionerTimezone = getPractitionerTimezone(selectedPractitionerId) || 'Australia/Adelaide';
      let apiStartDate = startDate;
      let apiEndDate = endDate;
      
      if (selectedTimezone !== practitionerTimezone) {
        console.log(`🔧 Converting save times from ${selectedTimezone} to ${practitionerTimezone}`);
        
        apiStartDate = convertDisplayTimeToPractitioner(startDate, selectedTimezone, practitionerTimezone);
        apiEndDate = convertDisplayTimeToPractitioner(endDate, selectedTimezone, practitionerTimezone);
        
        console.log(`🔧 Save conversion - Display: ${startDate.toISOString()} -> API: ${apiStartDate.toISOString()}`);
      }

      // Update unavailable block
      await updateUnavailableBlock(block.id, {
        practitionerId: selectedPractitionerId,
        startDate: apiStartDate.toISOString(),
        endDate: apiEndDate.toISOString(),
        notes: notes,
        unavailableBlockTypeId: blockTypeId || undefined
      });

      // Notify parent component that block was updated
      onBlockUpdated();
      
      // Close dialog
      onClose();
    } catch (err: any) {
      console.error('Error updating unavailable block:', err);
      setError(err.response?.data?.error || 'Failed to update unavailable block');
    } finally {
      setLoading(false);
    }
  };

  // Handle block deletion
  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    setDeleteLoading(true);
    setError(null);

    try {
      if (!block) {
        throw new Error('No block data to delete');
      }

      // Store the block ID for reference before closing dialog
      const blockId = block.id;
      
      // Delete unavailable block
      await deleteUnavailableBlock(blockId);
      
      // Log success for debugging
      console.log(`Successfully deleted unavailable block with ID: ${blockId}`);

      // First make a direct update to the week view if possible
      if ((window as any).removeUnavailableBlockFromWeekView) {
        console.log(`Directly removing block ${blockId} from week view via global function`);
        (window as any).removeUnavailableBlockFromWeekView(blockId);
      }

      // Close dialog
      onClose();
      
      // Notify parent component that block was deleted
      // This should update any other parts of the UI and refresh data if needed
      onBlockDeleted();
    } catch (err: any) {
      console.error('Error deleting unavailable block:', err);
      setError(err.response?.data?.error || 'Failed to delete unavailable block');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Reset delete confirmation if user changes their mind
  const handleCancelDelete = () => {
    setDeleteConfirm(false);
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <BlockIcon sx={{ color: 'error.main', mr: 1 }} />
            <Typography variant="h6" component="div">
              Edit Unavailable Block
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
        {startDate && (
          <Typography variant="subtitle2" color="text.secondary">
            {startDate.toLocaleDateString('en-AU', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        )}
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Block Type - simplified with radio buttons instead */}
          <FormControl fullWidth component="fieldset">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Block Type
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1,
              mt: 1
            }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  border: '1px solid',
                  borderColor: blockTypeId === 'lunch' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: blockTypeId === 'lunch' ? 'primary.light' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => setBlockTypeId('lunch')}
              >
                <Typography variant="body1" fontWeight={blockTypeId === 'lunch' ? 'bold' : 'normal'}>
                  Lunch Break (60 min)
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  p: 1.5, 
                  border: '1px solid',
                  borderColor: blockTypeId === 'meeting' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: blockTypeId === 'meeting' ? 'primary.light' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => setBlockTypeId('meeting')}
              >
                <Typography variant="body1" fontWeight={blockTypeId === 'meeting' ? 'bold' : 'normal'}>
                  Meeting / Appointment (30 min)
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  p: 1.5, 
                  border: '1px solid',
                  borderColor: blockTypeId === 'out' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: blockTypeId === 'out' ? 'primary.light' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => setBlockTypeId('out')}
              >
                <Typography variant="body1" fontWeight={blockTypeId === 'out' ? 'bold' : 'normal'}>
                  Out of Office (4 hours)
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  p: 1.5, 
                  border: '1px solid',
                  borderColor: blockTypeId === '' ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: blockTypeId === '' ? 'primary.light' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => setBlockTypeId('')}
              >
                <Typography variant="body1" fontWeight={blockTypeId === '' ? 'bold' : 'normal'}>
                  Custom Time Block
                </Typography>
              </Box>
            </Box>
          </FormControl>

          {/* Practitioner selection */}
          <FormControl fullWidth variant="outlined">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Practitioner
            </Typography>
            <Select
              value={selectedPractitionerId}
              onChange={(e) => setSelectedPractitionerId(e.target.value)}
              displayEmpty
            >
              {practitioners.map((practitioner) => (
                <MenuItem key={practitioner.id} value={practitioner.id}>
                  {practitioner.name || `${practitioner.first_name} ${practitioner.last_name}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date selection */}
          <FormControl fullWidth>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Date
            </Typography>
            
            {/* Calendar date picker - uses our local state */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
              <DateTimePicker
                label="Select Date"
                value={localSelectedDate}
                onChange={(newValue) => {
                  if (newValue) {
                    // Store the locally selected date
                    setLocalSelectedDate(newValue);
                    
                    // Keep the same time, just update the date
                    const currentDate = new Date(newValue);
                    
                    // Update start date with new selected date but keeping current time
                    if (startDate) {
                      const newStartDate = new Date(currentDate);
                      newStartDate.setHours(
                        startDate.getHours(),
                        startDate.getMinutes(),
                        0,
                        0
                      );
                      setStartDate(newStartDate);
                      
                      // Also update end date with the new date
                      if (endDate) {
                        const newEndDate = new Date(currentDate);
                        newEndDate.setHours(
                          endDate.getHours(),
                          endDate.getMinutes(),
                          0,
                          0
                        );
                        setEndDate(newEndDate);
                      }
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth />
                )}
                // Only show date picker, no time
                views={['day']}
                inputFormat="EEEE, d MMMM yyyy"
              />
            </LocalizationProvider>
          </FormControl>
          
          {/* Time selection with simple dropdown options */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Time Block
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Start time select */}
              <FormControl fullWidth>
                <InputLabel id="start-time-select-label">Start Time</InputLabel>
                <Select
                  labelId="start-time-select-label"
                  id="start-time-select"
                  value={startDate ? `${startDate.getHours()}:${startDate.getMinutes() || 0}` : ''}
                  label="Start Time"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    const newDate = new Date(localSelectedDate || new Date());
                    newDate.setHours(hours, minutes || 0, 0, 0);
                    console.log('Setting start date to:', newDate.toLocaleString(), 'from value:', e.target.value);
                    setStartDate(newDate);
                    
                    // Adjust end time if needed
                    if (endDate && newDate && endDate <= newDate) {
                      setEndDate(addHours(newDate, 1));
                    }
                  }}
                >
                  {/* Morning times (8AM - 11:30AM) */}
                  <MenuItem value="8:0">8:00 AM</MenuItem>
                  <MenuItem value="8:30">8:30 AM</MenuItem>
                  <MenuItem value="9:0">9:00 AM</MenuItem>
                  <MenuItem value="9:30">9:30 AM</MenuItem>
                  <MenuItem value="10:0">10:00 AM</MenuItem>
                  <MenuItem value="10:30">10:30 AM</MenuItem>
                  <MenuItem value="11:0">11:00 AM</MenuItem>
                  <MenuItem value="11:30">11:30 AM</MenuItem>
                  
                  {/* Noon */}
                  <MenuItem value="12:0">12:00 PM</MenuItem>
                  <MenuItem value="12:30">12:30 PM</MenuItem>
                  
                  {/* Afternoon (1PM - 4:30PM) */}
                  <MenuItem value="13:0">1:00 PM</MenuItem>
                  <MenuItem value="13:30">1:30 PM</MenuItem>
                  <MenuItem value="14:0">2:00 PM</MenuItem>
                  <MenuItem value="14:30">2:30 PM</MenuItem>
                  <MenuItem value="15:0">3:00 PM</MenuItem>
                  <MenuItem value="15:30">3:30 PM</MenuItem>
                  <MenuItem value="16:0">4:00 PM</MenuItem>
                  <MenuItem value="16:30">4:30 PM</MenuItem>
                  
                  {/* Evening (5PM - 8PM) */}
                  <MenuItem value="17:0">5:00 PM</MenuItem>
                  <MenuItem value="17:30">5:30 PM</MenuItem>
                  <MenuItem value="18:0">6:00 PM</MenuItem>
                  <MenuItem value="18:30">6:30 PM</MenuItem>
                  <MenuItem value="19:0">7:00 PM</MenuItem>
                  <MenuItem value="19:30">7:30 PM</MenuItem>
                  <MenuItem value="20:0">8:00 PM</MenuItem>
                  <MenuItem value="20:30">8:30 PM</MenuItem>
                </Select>
              </FormControl>
              
              {/* End time select */}
              <FormControl fullWidth>
                <InputLabel id="end-time-select-label">End Time</InputLabel>
                <Select
                  labelId="end-time-select-label"
                  id="end-time-select"
                  value={endDate ? `${endDate.getHours()}:${endDate.getMinutes() || 0}` : ''}
                  label="End Time"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    // Make sure to use the same date as the start date
                    const newDate = new Date(localSelectedDate || new Date());
                    newDate.setHours(hours, minutes || 0, 0, 0);
                    console.log('Setting end date to:', newDate.toLocaleString(), 'from value:', e.target.value);
                    setEndDate(newDate);
                  }}
                >
                  {/* Morning times (8AM - 11:30AM) */}
                  <MenuItem value="8:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 8 * 60)}>8:00 AM</MenuItem>
                  <MenuItem value="8:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 8 * 60 + 30)}>8:30 AM</MenuItem>
                  <MenuItem value="9:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 9 * 60)}>9:00 AM</MenuItem>
                  <MenuItem value="9:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 9 * 60 + 30)}>9:30 AM</MenuItem>
                  <MenuItem value="10:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 10 * 60)}>10:00 AM</MenuItem>
                  <MenuItem value="10:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 10 * 60 + 30)}>10:30 AM</MenuItem>
                  <MenuItem value="11:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 11 * 60)}>11:00 AM</MenuItem>
                  <MenuItem value="11:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 11 * 60 + 30)}>11:30 AM</MenuItem>
                  
                  {/* Noon */}
                  <MenuItem value="12:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 12 * 60)}>12:00 PM</MenuItem>
                  <MenuItem value="12:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 12 * 60 + 30)}>12:30 PM</MenuItem>
                  
                  {/* Afternoon (1PM - 4:30PM) */}
                  <MenuItem value="13:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 13 * 60)}>1:00 PM</MenuItem>
                  <MenuItem value="13:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 13 * 60 + 30)}>1:30 PM</MenuItem>
                  <MenuItem value="14:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 14 * 60)}>2:00 PM</MenuItem>
                  <MenuItem value="14:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 14 * 60 + 30)}>2:30 PM</MenuItem>
                  <MenuItem value="15:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 15 * 60)}>3:00 PM</MenuItem>
                  <MenuItem value="15:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 15 * 60 + 30)}>3:30 PM</MenuItem>
                  <MenuItem value="16:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 16 * 60)}>4:00 PM</MenuItem>
                  <MenuItem value="16:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 16 * 60 + 30)}>4:30 PM</MenuItem>
                  
                  {/* Evening (5PM - 9PM) */}
                  <MenuItem value="17:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 17 * 60)}>5:00 PM</MenuItem>
                  <MenuItem value="17:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 17 * 60 + 30)}>5:30 PM</MenuItem>
                  <MenuItem value="18:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 18 * 60)}>6:00 PM</MenuItem>
                  <MenuItem value="18:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 18 * 60 + 30)}>6:30 PM</MenuItem>
                  <MenuItem value="19:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 19 * 60)}>7:00 PM</MenuItem>
                  <MenuItem value="19:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 19 * 60 + 30)}>7:30 PM</MenuItem>
                  <MenuItem value="20:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 20 * 60)}>8:00 PM</MenuItem>
                  <MenuItem value="20:30" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 20 * 60 + 30)}>8:30 PM</MenuItem>
                  <MenuItem value="21:0" disabled={startDate && (startDate.getHours() * 60 + startDate.getMinutes() >= 21 * 60)}>9:00 PM</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Quick time duration buttons */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  if (startDate) {
                    setEndDate(addHours(startDate, 1));
                  }
                }}
              >
                1 hour
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  if (startDate) {
                    setEndDate(addHours(startDate, 2));
                  }
                }}
              >
                2 hours
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  if (startDate) {
                    setEndDate(addHours(startDate, 4));
                  }
                }}
              >
                4 hours
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  if (startDate) {
                    const newEnd = new Date(startDate);
                    newEnd.setHours(17, 0, 0, 0);
                    // Only set if newEnd is after startDate
                    if (newEnd > startDate) {
                      setEndDate(newEnd);
                    }
                  }
                }}
              >
                Until 5:00 PM
              </Button>
            </Box>
          </Box>

          {/* Notes */}
          <TextField
            label="Notes"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why is this time unavailable? (optional)"
          />
        </Stack>

        {error && (
          <Box sx={{ mt: 2, color: 'error.main' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        {deleteConfirm && (
          <Alert 
            severity="warning" 
            sx={{ mt: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={handleCancelDelete}
              >
                CANCEL
              </Button>
            }
          >
            Are you sure you want to delete this unavailable block? This action cannot be undone.
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleDelete}
          color="error"
          startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          disabled={loading || deleteLoading}
          sx={{ mr: 'auto' }}
        >
          {deleteConfirm ? 'Confirm Delete' : 'Delete'}
        </Button>

        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={loading || deleteLoading || !startDate || !endDate}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnavailableBlockEditDialog;